import { useEffect, useMemo, useRef, useState } from "react";

// MUI
import { Box, Collapse, Divider, Grid, Link, SelectChangeEvent } from "@mui/material";

// Project import
import SingleColumnInput from "components/input/singleColumnInput";
import TextFieldInput from "components/input/textField";
import { useTranslation } from "utils/locales/utilityFunctions";
import AutoCompleteGrouped from "components/input/autoCompleteGrouped";
import useEstatesAndMaps from "hooks/useEstatesAndMaps";
import useAuth from "hooks/useAuth";
import getDataOptions from "components/estatesAndMapSelecter/getDataOptions";
import Selector from "components/select/selector";
import {
  NEW_PROJECT_YEAR_RANGE,
  DEFAULT_PROJECT_STATUS,
  DEFAULT_PROJECT_STATUS_TYPE,
} from "config/config-general";
import { ProjectProps } from "types/estatesAndMap";
import useMapData from "hooks/useMapData";
import { sortAlphabetically } from "utils/sorting";
import TextInformation from "components/display/textInformation";

interface ProjectChooseOrCreateProps {
  openExt: boolean;
  withProjectCreate?: boolean;
  existingProjectData?: Partial<ProjectProps>;
  setOpenExt: (open: boolean) => void;
  signalCancel?: () => void;
  hideBackdrop?: boolean;
}
const ProjectChooseOrCreate = ({
  openExt,
  withProjectCreate,
  existingProjectData,
  setOpenExt,
  signalCancel,
  hideBackdrop,
}: ProjectChooseOrCreateProps) => {
  // --- Hooks ---  //
  const { translate } = useTranslation();
  const { user } = useAuth();
  const { activities, fetchActivities } = useMapData();
  const {
    projects,
    selectedProjectId,
    selectedEstateId,
    estates,
    orgEstates,
    setSelectedEstateId,
    setSelectedProjectId,
    saveProject,
  } = useEstatesAndMaps();

  // --- state --- //
  const [options, setOptions] = useState<any[]>([]);
  const [chosenProjectId, setChosenProjectId] = useState<string | null>(null);
  const [newProjectSectionOpen, setNewProjectSectionOpen] = useState(
    existingProjectData ? true : false,
  );
  const [estateId, setEstateId] = useState(
    existingProjectData && existingProjectData.estateId ? existingProjectData.estateId : "",
  );
  const [newProjectName, setNewProjectName] = useState(
    existingProjectData && existingProjectData.name ? existingProjectData.name : "",
  );
  const [newProjectStatus, setNewProjectStatus] = useState<DEFAULT_PROJECT_STATUS_TYPE>(
    existingProjectData && existingProjectData.projectStatus
      ? existingProjectData.projectStatus
      : "active",
  );
  const [newProjectYear, setNewProjectYear] = useState<number>(
    existingProjectData && existingProjectData.projectYear
      ? existingProjectData.projectYear
      : new Date().getFullYear(),
  );
  const [value, setValue] = useState<any[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saveButtonDisabled, setSaveButtonDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);

  // --- Refs --- //
  const groupByRef = useRef(true);
  const wasOpenRef = useRef(false);

  // --- Data --- //
  const PROJECT_STATUS = useMemo(() => {
    return DEFAULT_PROJECT_STATUS.map((el) => {
      return {
        id: el,
        label: translate(`project-status-${el}-label`),
      };
    });
  }, []);

  const ESTATES = useMemo(() => {
    return estates
      ? Object.values({ ...estates, ...orgEstates })
          .map((el) => {
            return {
              id: el.id,
              label: el.name,
            };
          })
          .sort((a, b) => sortAlphabetically(a.label, b.label))
      : [];
  }, [estates, orgEstates]);

  const PROJECT_YEARS = useMemo(() => {
    let startYear = newProjectYear;
    const currentYear = new Date().getFullYear();
    let diff = currentYear - startYear;
    if (currentYear < startYear) {
      startYear = currentYear;
      diff = 0;
    }
    let range = diff > 0 ? diff + NEW_PROJECT_YEAR_RANGE : NEW_PROJECT_YEAR_RANGE;
    const years = [];
    for (let i = 0; i <= range; i++) {
      years.push({
        id: (startYear + i).toString(),
        label: (startYear + i).toString(),
      });
    }
    return years;
  }, [newProjectYear]);

  // --- Effects --- //
  useEffect(() => {
    const newOptions = getDataOptions({
      user,
      estates,
      projects,
      selectedEstateId,
      allRoleOptions: [],
      externalText: translate("external").toLowerCase(),
    });
    setOptions(newOptions.list);
  }, [projects]);

  useEffect(() => {
    if (openExt && options) {
      options.forEach((el) => {
        if (!activities[el.projectId]) fetchActivities(el.projectId);
      });
    }
  }, [openExt]);

  useEffect(() => {
    if (selectedEstateId && estates[selectedEstateId]) {
      setEstateId(estates[selectedEstateId].id);
    }
  }, [selectedEstateId, estates]);

  useEffect(() => {
    if (options) {
      if (selectedProjectId) {
        const val = options.filter((el) => el.projectId === selectedProjectId);
        setValue(val);
        setChosenProjectId(selectedProjectId);
      }
    }
  }, [options, selectedProjectId]);

  useEffect(() => {
    if (openExt && chosenProjectId && !withProjectCreate) {
      if (!activities[chosenProjectId]) {
        setSubmitError(translate("activity-no-activities-in-project-error"));
        setSaveButtonDisabled(true);
      } else {
        setSubmitError(null);
        setSaveButtonDisabled(false);
      }
    }
  }, [chosenProjectId, activities, openExt]);

  useEffect(() => {
    if (!openExt) {
      wasOpenRef.current = false;
      return;
    }
    // Parent renders can supply a fresh defaults object while a save is pending.
    // Initialize only when opening, preserving the draft through save and close.
    if (wasOpenRef.current) return;
    wasOpenRef.current = true;
    resetState();
    setNewProjectSectionOpen(Boolean(existingProjectData));
    if (existingProjectData) {
      setNewProjectName(existingProjectData.name ? existingProjectData.name : "");
      setNewProjectStatus(
        existingProjectData.projectStatus ? existingProjectData.projectStatus : "active",
      );
      setNewProjectYear(
        existingProjectData.projectYear
          ? existingProjectData.projectYear
          : new Date().getFullYear(),
      );
      setEstateId(existingProjectData.estateId ? existingProjectData.estateId : "");
    }
  }, [openExt, existingProjectData]);

  // --- Functions --- //
  async function handleSave() {
    if (newProjectSectionOpen && newProjectName === "") {
      setSubmitError(translate("project-title-required"));
      return;
    } else if (!newProjectSectionOpen && chosenProjectId === null) {
      setSubmitError(translate("project-required"));
      return;
    } else if (estateId === "") {
      setSubmitError(translate("estate-required"));
      return;
    }
    setSubmitError(null);
    setLoading(true);
    // If existing data is present save existing project

    if (existingProjectData) {
      let estateName = estates[estateId]?.name || orgEstates[estateId]?.name || "";
      const data = {
        name: newProjectName,
        projectStatus: newProjectStatus,
        projectYear: newProjectYear,
        estateName: estateName,
        estateId: estateId,
      };
      const id = existingProjectData.id === "new" ? undefined : existingProjectData.id;
      const response = await saveProject(data, id);
      if (response && response.data && response.data.documentId) {
        setSelectedProjectId(response.data.documentId);
      }
      handleClose();
      return;
    }
    // If new project section is open save new project
    if (newProjectSectionOpen) {
      try {
        // Save new project
        const estateName = estates[estateId]?.name || orgEstates[estateId]?.name || "";
        const result = await saveProject({
          name: newProjectName,
          projectStatus: newProjectStatus,
          projectYear: newProjectYear,
          estateId: estateId,
          estateName: estateName,
        });
        // Set new project id as chosenProjectId
        if (result && result.data && result.data.documentId) {
          setSelectedEstateId(estateId);
          setSelectedProjectId(result.data.documentId);
          handleClose();
        } else {
          setSubmitError(translate("project-save-error"));
        }
      } catch (error: any) {
        setSubmitError(translate(error.meddate));
      }
    } else {
      const chosenProject = options.find((option) => option.projectId === chosenProjectId);
      if (chosenProject?.estateId) setSelectedEstateId(chosenProject.estateId);
      setSelectedProjectId(chosenProjectId);
      handleClose();
    }
  }

  const resetState = () => {
    setNewProjectName("");
    setNewProjectStatus("active");
    setNewProjectYear(new Date().getFullYear());
    setEstateId(selectedEstateId || "");
    setChosenProjectId(null);
    setValue([]);
    setSubmitError(null);
    setSaveButtonDisabled(false);
    setLoading(false);
  };

  const handleClose = () => {
    setOpenExt(false);
  };

  const handleCloseButton = () => {
    if (signalCancel) {
      setSubmitError(null);
      setSaveButtonDisabled(false);
      signalCancel();
      return;
    }
    handleClose();
  };

  const autoselectChangeHandler = (e: any, v: any) => {
    setNewProjectSectionOpen(false);
    if (v.length === 0) {
      setChosenProjectId(null);
      setSubmitError(null);
      setValue([]);
      return;
    }
    const lastItem = v.slice(-1)[0];
    setChosenProjectId(lastItem.projectId);
    setValue([lastItem]);
  };

  const handleChangeNewProjectName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewProjectName(e.target.value);
  };

  const handleNewProjectContentOpen = () => {
    if (value.length > 0) setValue([]);
    setNewProjectSectionOpen(!newProjectSectionOpen);
  };

  const handleChangeEstate = (e: SelectChangeEvent<string>) => {
    setEstateId(e.target.value);
  };

  const handleChangeProjectStatus = (e: SelectChangeEvent<string>) => {
    setNewProjectStatus(e.target.value as DEFAULT_PROJECT_STATUS_TYPE);
  };

  const handleChangeProjectYear = (e: SelectChangeEvent<string>) => {
    setNewProjectYear(parseInt(e.target.value));
  };

  // Additional jsx content
  const newProjectContent = () => {
    return (
      <Grid container spacing={{ xs: 3 }} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <TextFieldInput
            title={translate("title")}
            value={newProjectName}
            showLabel={true}
            fullWidth={true}
            handleChange={handleChangeNewProjectName}
            required={true}
            disabled={loading || newProjectStatus === "archived"}
            error={submitError !== null && newProjectName === ""}
          />
        </Grid>

        <Grid item xs={12}>
          <Selector
            title={translate("estate")}
            value={estateId}
            options={ESTATES}
            label={true}
            fullWidth={true}
            changeHandler={handleChangeEstate}
            required={true}
            disabled={loading || newProjectStatus === "archived" || (existingProjectData && existingProjectData.id !== "new")}
          />
        </Grid>

        <Grid item xs={12}>
          <Selector
            title={translate("status")}
            value={newProjectStatus}
            options={PROJECT_STATUS}
            label={true}
            fullWidth={true}
            changeHandler={handleChangeProjectStatus}
            required={false}
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12}>
          <Selector
            title={translate("project-year")}
            value={newProjectYear ? newProjectYear.toString() : ""}
            options={PROJECT_YEARS}
            label={true}
            fullWidth={true}
            changeHandler={handleChangeProjectYear}
            required={false}
            disabled={loading || newProjectStatus === "archived"}
          />
        </Grid>

        {existingProjectData?.id && existingProjectData?.id !== "new" && (
          <Grid item xs={12}>
            <TextInformation label={translate("project-id")} data={existingProjectData?.id} />
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <SingleColumnInput
      title={translate("project")}
      //captionText={existingProjectData ? undefined : translate("project-choose-or-open-dialog-caption-text")}
      sectionHeader={
        existingProjectData
          ? existingProjectData.id === "new"
            ? translate("create")
            : translate("change")
          : translate("project-choose-or-open-dialog-header-text")
      }
      open={openExt}
      handleClose={handleCloseButton}
      handleSave={() => handleSave()}
      saveDisabled={loading || saveButtonDisabled}
      buttonText={
        existingProjectData
          ? existingProjectData.id === "new"
            ? translate("create")
            : translate("save")
          : translate("project-choose-or-open-dialog-button-text")
      }
      errorText={submitError ? submitError : undefined}
      loading={loading}
      hideBackdrop={hideBackdrop}
    >
      <Grid container spacing={{ xs: 2 }}>
        {/* ------ Section for existing project ------ */}
        {!existingProjectData && (
          <Grid item xs={12} sx={{ mt: 2 }}>
            <AutoCompleteGrouped
              title="title"
              // disabled={newProjectSectionOpen}
              label={translate("choose-project")}
              showTitle={false}
              showLabel={false}
              data={options}
              groupByField={groupByRef.current ? "estateName" : undefined}
              renderOptionField={"projectName"}
              renderTagField={"tagField"}
              noOptionsText={translate("no-options")}
              changeHandler={autoselectChangeHandler}
              value={value}
              multiple={true}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Box sx={{ mb: 0 }} />
        </Grid>

        {/* ------ Section for new project ------ */}
        {withProjectCreate && (
          <>
            {!existingProjectData && (
              <>
                <Grid item xs={12}>
                  <Divider>{translate("or")}</Divider>
                </Grid>

                {!newProjectSectionOpen && (
                  <Grid item xs={12}>
                    <Link
                      onClick={handleNewProjectContentOpen}
                      underline="always"
                      style={{ cursor: "pointer" }}
                    >
                      {translate("create-new-project")}
                    </Link>
                  </Grid>
                )}

                {newProjectSectionOpen && (
                  <Grid item xs={12}>
                    <Box sx={{ mb: 0 }} />
                  </Grid>
                )}
              </>
            )}

            <Grid item xs={12}>
              <Collapse in={newProjectSectionOpen} timeout="auto" unmountOnExit>
                {newProjectContent()}
              </Collapse>
            </Grid>
          </>
        )}
      </Grid>
    </SingleColumnInput>
  );
};

export default ProjectChooseOrCreate;
